import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  XCircle,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Typography,
} from '@roger/ui-components';
import {
  client,
  getBillingCycleEoePreview,
  getBillingCycleHippsPreview,
} from '../../services/api';
import type {
  EoePreviewResponse,
  GetBillingCyclesResponse,
} from '../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';

export interface EOEPreviewPopupProps {
  billingCycle: GetBillingCyclesResponse | null;
  onOpenChange: (open: boolean) => void;
}

const EOEPreviewPopup: React.FC<EOEPreviewPopupProps> = ({
  billingCycle,
  onOpenChange,
}) => {
  const [preview, setPreview] = useState<EoePreviewResponse | null>(null);
  const [hippsCode, setHippsCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPreview = async () => {
      if (!billingCycle) {
        setPreview(null);
        setHippsCode(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const [previewResponse, hippsResponse] = await Promise.all([
          getBillingCycleEoePreview({
            path: { billing_cycle_id: billingCycle.billing_cycle_id },
            client,
          }),
          getBillingCycleHippsPreview({
            path: { billing_cycle_id: billingCycle.billing_cycle_id },
            client,
          }),
        ]);
        setPreview(previewResponse?.data ?? null);
        setHippsCode(hippsResponse?.data?.hipps_code ?? null);
      } catch (err) {
        console.error('Failed to load EOE preview:', err);
        setHippsCode(null);
        setError('Unable to load billing cycle details.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreview();
  }, [billingCycle]);

  const getMiddleInitial = (middleName?: string | null): string =>
    middleName?.trim()?.charAt(0)?.toUpperCase() || '-';

  const displayValue = (value?: string | null): string =>
    value && value.trim() !== '' ? value : '-';

  const displayDate = (value?: string | null): string =>
    formatDateToMMDDYYYY(value) || '-';

  const demographics = preview?.patient_demographics;
  const demographicsRows = demographics
    ? [
        { label: 'Last name', value: displayValue(demographics.last_name) },
        { label: 'First name', value: displayValue(demographics.first_name) },
        {
          label: 'Middle initial',
          value: getMiddleInitial(demographics.middle_name),
        },
        { label: 'Gender', value: displayValue(demographics.gender) },
        {
          label: 'Date of birth',
          value: displayDate(demographics.date_of_birth),
        },
        {
          label: 'Medicare number',
          value: displayValue(demographics.medicare_number),
        },
        {
          label: 'Medical record number',
          value: displayValue(demographics.mrn),
        },
        { label: 'Phone number', value: displayValue(demographics.phone) },
      ]
    : [];

  const address = preview?.patient_address;
  const addressRows = address
    ? [
        { label: 'Address', value: displayValue(address.address) },
        { label: 'City', value: displayValue(address.city) },
        { label: 'State', value: displayValue(address.state) },
        { label: 'Zip', value: displayValue(address.zip_code) },
      ]
    : [];

  const physician = preview?.physician_information;
  const physicianRows = physician
    ? [
        { label: 'Last name', value: displayValue(physician.last_name) },
        { label: 'First name', value: displayValue(physician.first_name) },
        { label: 'Physician NPI', value: displayValue(physician.npi) },
        { label: 'PECOS status', value: displayValue(physician.pecos_status) },
      ]
    : [];

  const episodeInfo = preview?.episode_information;
  const episodeInformationRows = [
    { label: 'Type of bill', value: displayValue(episodeInfo?.type_of_bill) },
    {
      label: 'Start of care date',
      value: displayDate(episodeInfo?.soc_date),
    },
    {
      label: 'Assessment date',
      value: displayDate(episodeInfo?.assessment_date),
    },
    {
      label: 'Episode start date',
      value: displayDate(episodeInfo?.episode_start_date),
    },
    {
      label: 'First billable visit',
      value: displayDate(episodeInfo?.first_billable_visit),
    },
  ];

  const admissionInfo = preview?.admission_information;
  const admissionInformationRows = [
    {
      label: 'Point of origin',
      value: displayValue(admissionInfo?.point_of_origin),
    },
    {
      label: 'Admission type',
      value: displayValue(admissionInfo?.admission_type),
    },
    {
      label: 'Condition code',
      value: displayValue(admissionInfo?.condition_code),
    },
    {
      label: 'Admission diagnosis code',
      value: displayValue(admissionInfo?.diagnosis_code),
    },
    { label: 'Value code', value: displayValue(admissionInfo?.value_code) },
    {
      label: 'Value code amount',
      value: displayValue(admissionInfo?.value_code_amount),
    },
  ];

  const validationItems = billingCycle
    ? (() => {
        type ValidationStatus = 'success' | 'error' | 'warning';
        const items: Array<{
          label: string;
          status: ValidationStatus;
          message: string;
        }> = [];

        items.push(
          billingCycle.all_oasis_correct_state
            ? {
                label: 'OASIS',
                status: 'success',
                message: 'All OASIS assessments are in the correct state',
              }
            : {
                label: 'OASIS',
                status: 'error',
                message: 'Not all OASIS assessments are in the correct state',
              }
        );

        items.push(
          hippsCode && hippsCode.trim() !== ''
            ? {
                label: 'HIPPS',
                status: 'success',
                message: hippsCode,
              }
            : {
                label: 'HIPPS',
                status: 'error',
                message: 'HIPPS code could not be generated',
              }
        );

        items.push(
          billingCycle.all_visits_completed
            ? {
                label: 'Visits',
                status: 'success',
                message: 'All billable visits are completed',
              }
            : {
                label: 'Visits',
                status: 'error',
                message: 'Not all billable visits are in a completed state',
              }
        );

        items.push(
          billingCycle.all_orders_signed
            ? {
                label: 'Orders',
                status: 'success',
                message: 'All orders are signed',
              }
            : {
                label: 'Orders',
                status: 'warning',
                message:
                  'Not all orders are in a returned with signature status',
              }
        );

        items.push(
          billingCycle.is_f2f_valid
            ? {
                label: 'F2F',
                status: 'success',
                message: 'F2F document is valid',
              }
            : {
                label: 'F2F',
                status: 'error',
                message: 'Document not attached or signed',
              }
        );

        if (!billingCycle.is_medicare_number_required) {
          items.push({
            label: 'Medicare number',
            status: 'success',
            message: 'Medicare number not required for this claim',
          });
        } else if (!billingCycle.is_medicare_number_valid) {
          items.push({
            label: 'Medicare number',
            status: 'error',
            message: 'Medicare number is missing',
          });
        } else {
          items.push({
            label: 'Medicare number',
            status: 'success',
            message: 'Medicare number is valid',
          });
        }

        return items;
      })()
    : [];

  const renderOasisTables = () => {
    const visits = preview?.oasis_visits ?? [];
    if (visits.length === 0) {
      return (
        <div className='mx-6 mb-3 ml-10 rounded border border-slate-200 bg-white px-4 py-3'>
          <span className='text-xs text-slate-500'>No OASIS visits found</span>
        </div>
      );
    }
    return visits.map((visit, visitIndex) => (
      <div
        key={visitIndex}
        className='mx-6 mb-2 ml-10 overflow-hidden rounded border border-slate-200'
      >
        <div className='border-b border-slate-200 bg-slate-100 px-4 py-1.5'>
          <span className='text-xs font-semibold uppercase tracking-wider text-slate-500'>
            {visit.task}
          </span>
        </div>
        {[
          { label: 'Status', value: displayValue(visit.status) },
          { label: 'Assigned', value: displayValue(visit.assigned_user) },
          { label: 'Target date', value: displayDate(visit.target_date) },
          { label: 'Visit date', value: displayDate(visit.visit_date) },
        ].map((row, i) => (
          <div
            key={row.label}
            className={`grid grid-cols-[130px_1fr] gap-4 px-4 py-1.5 text-xs ${
              i % 2 === 0 ? 'bg-white' : 'bg-slate-50'
            }`}
          >
            <span className='font-medium text-slate-600'>{row.label}</span>
            <span className='text-slate-800'>{row.value}</span>
          </div>
        ))}
      </div>
    ));
  };

  const renderValidationSection = () => (
    <Card className='overflow-hidden p-0'>
      <CardHeader variant='highlighted'>
        <CardTitle variant='highlighted'>EOE Validation</CardTitle>
      </CardHeader>
      <CardContent className='py-4'>
        <div className='space-y-0'>
          {validationItems.map((item, index) => (
            <React.Fragment key={item.label}>
              <div
                className={`grid grid-cols-[16px_160px_1fr] items-center gap-3 px-6 py-2.5 ${
                  index % 2 === 0 ? 'bg-white' : 'bg-slate-100'
                }`}
              >
                <div className='flex items-center'>
                  {item.status === 'success' && (
                    <CheckCircle2 className='h-4 w-4 text-green-600' />
                  )}
                  {item.status === 'error' && (
                    <XCircle className='h-4 w-4 text-red-600' />
                  )}
                  {item.status === 'warning' && (
                    <AlertTriangle className='h-4 w-4 text-yellow-500' />
                  )}
                </div>
                <span className='text-slate-700 font-medium text-sm'>
                  {item.label}
                </span>
                <span
                  className={`text-sm ${
                    item.status === 'success'
                      ? 'text-slate-900'
                      : item.status === 'error'
                        ? 'text-red-700'
                        : 'text-yellow-700'
                  }`}
                >
                  {item.message}
                </span>
              </div>
              {item.label === 'OASIS' ? renderOasisTables() : null}
            </React.Fragment>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const renderSection = (
    title: string,
    rows: Array<{ label: string; value: string }>
  ) => (
    <Card className='overflow-hidden p-0'>
      <CardHeader variant='highlighted'>
        <CardTitle variant='highlighted'>{title}</CardTitle>
      </CardHeader>
      <CardContent className='py-4'>
        <div className='space-y-0'>
          {rows.map((row, index) => (
            <div
              key={row.label}
              className={`grid grid-cols-[200px_1fr] items-baseline gap-6 px-6 py-2.5 ${
                index % 2 === 0 ? 'bg-white' : 'bg-slate-100'
              }`}
            >
              <Typography
                as='span'
                className='text-slate-700 font-medium text-sm'
              >
                {row.label}
              </Typography>
              <Typography as='span' className='text-slate-900 text-sm'>
                {row.value}
              </Typography>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const renderOasisIcd10Card = (): React.ReactNode => {
    if (!preview) {
      return null;
    }
    const section = preview.oasis_diagnoses_from_visit;
    return (
      <Card className='overflow-hidden p-0'>
        <CardHeader variant='highlighted'>
          <CardTitle variant='highlighted'>Diagnosis Information</CardTitle>
        </CardHeader>
        <CardContent className='py-4'>
          {!section ? (
            <Typography
              variant='small'
              as='p'
              className='px-6 text-muted-foreground'
            >
              No diagnosis codes on OASIS visits linked to this billing cycle.
            </Typography>
          ) : (
            <>
              <Typography
                variant='small'
                as='p'
                className='px-6 pb-3 text-slate-600'
              >
                Latest visit with diagnoses: {section.source_visit_task}
                {section.source_visit_date
                  ? ` · ${displayDate(section.source_visit_date)}`
                  : null}
              </Typography>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-slate-200 bg-slate-100'>
                    <th className='text-left px-6 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide'>
                      Role
                    </th>
                    <th className='text-left px-6 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide'>
                      Code
                    </th>
                    <th className='text-left px-6 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide'>
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {section.diagnoses.map((dx, index) => (
                    <tr
                      key={`${dx.code}-${dx.position}`}
                      className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                    >
                      <td className='px-6 py-2.5 text-slate-900 whitespace-nowrap'>
                        {dx.is_primary ? 'Primary' : 'Secondary'}
                      </td>
                      <td className='px-6 py-2.5 font-mono text-slate-900 whitespace-nowrap'>
                        {dx.code}
                      </td>
                      <td className='px-6 py-2.5 text-slate-900'>
                        {displayValue(dx.description)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={billingCycle !== null} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[80vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>EOE Claim Preview</DialogTitle>
        </DialogHeader>
        <div className='flex flex-col gap-4'>
          {isLoading ? (
            <div className='flex flex-1 flex-col items-center justify-center py-10 text-center'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
              <Typography variant='small' as='p' className='mt-2'>
                Loading billing cycle details...
              </Typography>
            </div>
          ) : null}

          {!isLoading && error ? (
            <Alert className='border-red-200 bg-red-50'>
              <AlertCircle className='h-4 w-4 text-red-600' />
              <AlertDescription className='text-red-800'>
                <p>
                  An error happened while loading billing cycle details. Please
                  try again and contact support if the issue happens again.
                </p>
                <p className='mt-2'>{error}</p>
              </AlertDescription>
            </Alert>
          ) : null}

          {!isLoading && !error && preview ? (
            <>
              {renderSection('Patient Demographics', demographicsRows)}
              {renderSection('Patient Address', addressRows)}
              {renderSection('Physician Information', physicianRows)}
              {renderSection('Episode Information', episodeInformationRows)}
              {renderSection('Admission Information', admissionInformationRows)}
              {renderOasisIcd10Card()}
            </>
          ) : null}

          {!isLoading && billingCycle ? renderValidationSection() : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EOEPreviewPopup;
